/**
 * Convert a positive integer to its Hebrew numeral (gematriya) form,
 * including geresh (׳) for single-letter numerals and gershayim (״)
 * before the last letter for multi-letter numerals.
 *
 * Examples: 1 → א׳, 11 → י״א, 15 → ט״ו, 16 → ט״ז, 26 → כ״ו, 100 → ק׳, 115 → קט״ו.
 *
 * 15 and 16 use טו/טז (not יה/יו) to avoid spelling divine names.
 * Falls back to the input as a string for non-positive integers.
 */
export function toHebrewNumeral(n: number): string {
  if (!Number.isInteger(n) || n <= 0) {
    return String(n)
  }

  const hundredsMap = ['', 'ק', 'ר', 'ש', 'ת']
  const tensMap = ['', 'י', 'כ', 'ל', 'מ', 'נ', 'ס', 'ע', 'פ', 'צ']
  const onesMap = ['', 'א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ז', 'ח', 'ט']

  let bare = ''
  let num = n

  while (num >= 400) {
    bare += 'ת'
    num -= 400
  }

  if (num >= 100) {
    const h = Math.floor(num / 100)
    bare += hundredsMap[h]
    num -= h * 100
  }

  if (num === 15) {
    bare += 'טו'
  } else if (num === 16) {
    bare += 'טז'
  } else {
    if (num >= 10) {
      const t = Math.floor(num / 10)
      bare += tensMap[t]
      num -= t * 10
    }
    if (num > 0) {
      bare += onesMap[num]
    }
  }

  if (bare.length === 1) {
    return bare + '׳'
  }
  return bare.slice(0, -1) + '״' + bare.slice(-1)
}

/**
 * Vocalized "פֶּרֶק" with etnachta — used as the chapter heading prefix
 * (e.g., "פֶּרֶק֑ א׳") to give the label a biblical/Tanakh feel.
 */
const CHAPTER_WORD = 'פֶּרֶק֑'

/** Format a chapter ordinal as "פֶּרֶק֑ א׳", "פֶּרֶק֑ י״א", etc. */
export function formatChapterLabel(n: number): string {
  return `${CHAPTER_WORD} ${toHebrewNumeral(n)}`
}
